import { useSetRebuyItems } from "@babackapp/return-widget-components/hooks/use-set-rebuy-items";
import { useFinancialSummary } from "@babackapp/return-widget-components/hooks/use-financial-summary";
import { useDraftReturn } from "@babackapp/return-widget-components/hooks/use-draft-return";
import { usePublicOrder } from "@babackapp/return-widget-components/queries/use-public-order";
import type { DraftLineItem, OrderLineItem } from "@babackapp/return-widget-components/types";
import type {
  CartItem,
  EmbedToWidgetMessage,
} from "@babackapp/return-widget-components/types/shop-now";
import { publicGetVariantPricing } from "@babackapp/private-api-client/client/public-get-variant-pricing";
import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { postToParent } from "../lib/shop-now-embed";
import { useWidgetParams } from "./use-widget-params";

type UseShopNowResolveResult = {
  items: CartItem[];
  currency: string;
  cartTotal: number;
  /** 1 + tax rate for HT shops. Apply to item.price * item.quantity to get TTC sub-total. */
  taxMultiplier: number;
  exchangeCreditLineItems: DraftLineItem[];
  orderLineItems: OrderLineItem[];
  handleAddMore: () => void;
  handleConfirm: () => void;
};

/**
 * Overlay current catalogue prices onto cart items fetched from /cart.js.
 *
 * BBS-193: /cart.js prices may be stale if the customer loaded the rebuy banner
 * before a price change. The catalogue (catalogProductVariantPricing) corrects
 * stale /cart.js prices in the display. The backend uses Shopify Admin live prices
 * (draftOrderCalculate without priceOverride) — display and charge are aligned when
 * the catalogue is fresh (delta sync every 30 min).
 *
 * Variants absent from the catalogue keep their original /cart.js price.
 */
async function refreshPrices(
  shopId: string,
  items: CartItem[],
): Promise<CartItem[]> {
  try {
    const variantIds = items.map((i) => String(i.variantId));
    const pricings = await publicGetVariantPricing(shopId, variantIds);
    const priceMap = new Map(pricings.map((p) => [p.variantId, p.price]));
    return items.map((item) => {
      const freshPrice = priceMap.get(String(item.variantId));
      return freshPrice != null ? { ...item, price: freshPrice } : item;
    });
  } catch {
    // Network error or catalogue unavailable — fall back to /cart.js prices.
    // The backend will still charge the correct amount; only the display may be off.
    return items;
  }
}

export function useShopNowResolve(): UseShopNowResolveResult {
  const navigate = useNavigate();
  const { orderId, shopInternalId } = useWidgetParams();
  const { data: order } = usePublicOrder(orderId);
  const financialSummary = useFinancialSummary(orderId, order ?? null);
  const setRebuyItems = useSetRebuyItems(orderId);
  const draft = useDraftReturn(orderId);

  const exchangeCreditLineItems = (draft?.lineItems ?? []).filter(
    (li) => li.resolution === "exchange-credit",
  );
  const orderLineItems: OrderLineItem[] = order?.lineItems ?? [];
  const orderCurrency = order?.currency?.shopCurrency ?? "EUR";

  // Items live directly in the draft — no intermediate sessionStorage buffer.
  const items = draft?.rebuyItems ?? [];

  // Generation counter: incremented on every refresh start to discard superseded results.
  const refreshGen = useRef(0);

  useEffect(() => {
    postToParent({ type: "WIDGET_SHOP_NOW_READY" });
  }, []);

  // Refresh stale prices on mount (handles page refresh after shopping).
  useEffect(() => {
    if (!items.length) return;
    const gen = ++refreshGen.current;
    refreshPrices(shopInternalId, items).then((refreshed) => {
      if (refreshGen.current !== gen) return;
      setRebuyItems(refreshed);
    });
  }, []); // intentionally run once on mount

  const applyItems = useCallback(
    async (raw: CartItem[]) => {
      const gen = ++refreshGen.current;
      const refreshed = await refreshPrices(shopInternalId, raw);
      if (refreshGen.current !== gen) return;
      setRebuyItems(refreshed);
    },
    [shopInternalId, setRebuyItems],
  );

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      const msg = event.data as EmbedToWidgetMessage;
      if (msg?.type === "SHOP_NOW_RESUME") {
        void applyItems(msg.items);
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [applyItems]);

  const cartTotal = items.reduce(
    (sum, item) =>
      sum + item.price * item.quantity * financialSummary.taxMultiplier,
    0,
  );

  const handleAddMore = () =>
    postToParent({ type: "SHOP_NOW_START", clearCart: false });

  const handleConfirm = () => {
    // items already committed to draft.rebuyItems — just navigate
    const remaining = financialSummary.exchangeCreditTotal - cartTotal;
    navigate(remaining > 0 ? "../shop-now-remaining-credit" : "../return-mode");
  };

  return {
    items,
    currency: orderCurrency,
    cartTotal,
    taxMultiplier: financialSummary.taxMultiplier,
    exchangeCreditLineItems,
    orderLineItems,
    handleAddMore,
    handleConfirm,
  };
}
